function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// @ts-strict-ignore
import * as React from 'react';
import throttle from 'lodash/throttle';
import ResizeObserver from 'resize-observer-polyfill';
import sum from 'lodash/sum';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import debounce from 'lodash/debounce';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { withStyles } from 'common/util/react';
import { assignColorsToSegmentArray, BAR_TEXT_LABEL_WIDTH, generateHeaders, getResponsiveColumnWidths, maximumSegmentValue, MIN_BAR_SEGMENT_WIDTH, MINIMUM_COLUMN_WIDTH, minimumSegmentValue, segmentDepth, segmentHasChildren, truncateSegments } from 'common/charts/visualization/bar-viz/util';
import { TooltipReact } from 'common/components/tooltip';
import { SvgIconReact } from 'common/components/svg-icon';
import { TOOLTIP_THROTTLE_TIME } from 'common/components/chart-tooltip/util';
import { SelectReact } from 'common/components/select';
import { getTooltipFromLabel } from 'common/report/util/properties';
import { FlatSortConfig, SortBy } from 'common/types/reports/bookmark';
import { renderSegmentLabel } from 'common/templates/mixins-react';
import { Placement } from 'common/components/tooltip/types';
import { ChartTooltip, ChartTooltipJsxProps } from 'common/components/chart-tooltip';
import { getSeriesMatchPath } from 'common/charts/contexts/utils';
import { getBarBackground, getBarChartDisplayValues } from 'common/charts/visualization/bar-viz/util';
import { getMaxLabelCharLengthFromSegments } from 'common/report/insights/charts/insights-bar-chart/util';
import { Bar, BarSegment, BarVizProps, ColumnWidths, Segment, SegmentWithChildren, SegmentWithoutChildren } from './types';
import styles from './index.module.styl';
import css from './index.module.styl?inline';
const DOWNLOAD_SEGMENT_LIMIT = 30;

// See: $bar-label-padding
const BAR_LABEL_PADDING = 4;
const PX_PER_CHAR_LENGTH = 7.5;

// tweak this to optimize if needed, more or less arbitrary for now
function getProgressivePageSize(height: number): number {
  if (height < 600) {
    return 20;
  }
  return 100;
}
export const BarViz: React.FC<BarVizProps> = props => {
  const [chartContentResizeObserver, setChartContentResizeObserver] = useState<ResizeObserver>(null);
  const [chartData, setChartData] = useState<Segment[]>([]);
  const [chartMax, setChartMax] = useState<number>(0);
  const [chartMin, setChartMin] = useState<number>(0);
  const [columnWidths, setColumnWidths] = useState<ColumnWidths>({});
  const [contentIsTruncated, setContentIsTruncated] = useState<boolean>(false);
  const [hoverTooltip, setHoverTooltip] = useState<ChartTooltipJsxProps | null>(null);
  const [progressiveIntersectionObserver, setProgressiveIntersectionObserver] = useState<IntersectionObserver>(null);
  const [progressivePage, setProgressivePage] = useState<number>(0);
  const [resizeColumn, setResizeColumn] = useState<HTMLElement | null>(null);
  const [resizeColumnIdx, setResizeColumnIdx] = useState<number | null>(null);
  const [resizeColumnWidth, setResizeColumnWidth] = useState<number | null>(null);
  const [resizeStartX, setResizeStartX] = useState<number | null>(null);
  const [segmentsToDisplay, setSegmentsToDisplay] = useState<Segment[]>([]);
  const [selfWidth, setSelfWidth] = useState<number>(0);
  const [selfHeight, setSelfHeight] = useState<number>(0);
  const [maxLabelCharLength, setMaxLabelCharLength] = useState<number>(0);
  const [maxNegativeLabelCharLength, setMaxNegativeLabelCharLength] = useState<number>(0);
  const hasNegativeValues = chartMin < 0;
  const hideTooltipTimeoutId = useRef<number | null>(null);

  // https://react.dev/learn/manipulating-the-dom-with-refs#how-to-manage-a-list-of-refs-using-a-ref-callback
  const headersRef = useRef<Array<HTMLDivElement | null>>(null);
  function getHeaders() {
    if (!headersRef.current) {
      // Initialize the array on first usage.
      headersRef.current = [];
    }
    return headersRef.current;
  }
  const rootRef = useRef<HTMLDivElement>(null);
  const progressiveMarkerRef = useRef<HTMLDivElement>(null);
  const chartContentRef = useRef<HTMLDivElement>(null);
  const getHeaderColumn = (index: number): HTMLElement => {
    return getHeaders()[index];
  };
  const getPercentageWidth = (widthInPx: number) => {
    return widthInPx / selfWidth * 100;
  };
  const getPixelWidth = (percentage: number) => {
    return percentage / 100 * selfWidth;
  };
  const getRespColumnWidths = () => {
    const depth = segmentDepth(chartData[0]) + 1;
    return getResponsiveColumnWidths(selfWidth, depth);
  };
  const matchesHoveredPath = (path: string[]) => {
    const hoveredSeries = props.segmentFilterController.hoveredSeries;
    if (!hoveredSeries || !path) {
      return false;
    }
    return getSeriesMatchPath(path.at(-1)) === getSeriesMatchPath(hoveredSeries.seriesId);
  };
  const onSortingChange = (ev, idx): void => {
    var _props$onSortingChang;
    const value: FlatSortConfig = ev.detail.item.value;
    const depth = segmentDepth(chartData[0]);
    let newSortConfig = props.sortConfig;
    if (idx > depth) {
      newSortConfig = _objectSpread(_objectSpread({}, newSortConfig), {}, {
        sortBy: SortBy.Value,
        sortOrder: value.sortOrder
      });
    } else {
      newSortConfig.colSortAttrs[idx] = value;
      newSortConfig.sortBy = SortBy.Column;
    }
    (_props$onSortingChang = props.onSortingChange) === null || _props$onSortingChang === void 0 || _props$onSortingChang.call(props, newSortConfig);
  };
  const clearHideTooltipTimeout = () => {
    if (hideTooltipTimeoutId.current) {
      window.clearTimeout(hideTooltipTimeoutId.current);
      hideTooltipTimeoutId.current = null;
    }
  };
  const onSegmentMouseMove = (ev: MouseEvent, segment: SegmentWithoutChildren, bar: Bar, barSegment: BarSegment) => {
    clearHideTooltipTimeout();
    if (props.hideTooltip) {
      return;
    }
    const tooltipParams = props.tooltipDataCallback(segment, bar, barSegment);
    setHoverTooltip({
      props: _objectSpread(_objectSpread({}, tooltipParams), {}, {
        positioning: {
          clientX: ev.clientX,
          clientY: ev.clientY
        },
        renderInPortal: true
      })
    });
  };
  const getColumnWidth = (columnIdx: number): number => {
    const columnPixelWidth = getPixelWidth(getRespColumnWidths().labelWidthPercentage);
    if (!canResize()) {
      return columnPixelWidth;
    }
    if (resizeColumnIdx === columnIdx) {
      return resizeColumnWidth;
    }
    return columnWidths[columnIdx] ? getPixelWidth(columnWidths[columnIdx]) : columnPixelWidth;
  };
  const getColumnWidths = (): number[] => {
    return Array(segmentDepth(chartData[0]) + 1).fill(null).map((_, idx) => getColumnWidth(idx));
  };
  const calculateMaxColumnWidth = (columnIdx: number): number => {
    const otherColumnWidth = getColumnWidths().reduce((acc, curr, idx) => idx === columnIdx ? acc : acc + curr, 0);

    // account for min width of a single bar segment, label width, and 2px buffer
    let valueBuffer = BAR_TEXT_LABEL_WIDTH + MIN_BAR_SEGMENT_WIDTH + 2;
    if (hasNegativeValues) {
      valueBuffer *= 2;
    }
    return selfWidth - otherColumnWidth - valueBuffer;
  };
  const setDefaultColumnWidths = () => {
    if (!(chartData !== null && chartData !== void 0 && chartData.length) || !selfWidth) {
      return;
    }
    let updatedColumnWidths;
    if (!isEmpty(columnWidths)) {
      updatedColumnWidths = columnWidths;
    } else {
      updatedColumnWidths = props.columnWidths;
    }
    if (!isEmpty(updatedColumnWidths)) {
      const minWidth = getPercentageWidth(MINIMUM_COLUMN_WIDTH);
      const boundColumnWidths = Object.entries<number>(updatedColumnWidths).map(_ref => {
        let [indexKey, width] = _ref;
        const maxWidth = getPercentageWidth(calculateMaxColumnWidth(parseInt(indexKey)));
        return [indexKey, Math.min(Math.max(width, minWidth), maxWidth)];
      });
      setColumnWidths(Object.fromEntries(boundColumnWidths));
    }
  };
  const onChartContentInserted = (chartContentEl: HTMLElement) => {
    const debouncedResizeUpdate = debounce((width, height) => {
      setSelfWidth(width);
      setSelfHeight(height);

      // always snap scroll to the right when resizing to fit a certain number of visible segments
      if (chartContentEl) {
        requestAnimationFrame(() => chartContentEl.scrollLeft = chartContentEl.scrollWidth);
      }
      setDefaultColumnWidths();
    }, 50, {
      leading: true,
      trailing: true
    });
    const newObserver = new ResizeObserver(entries => {
      const {
        width,
        height
      } = entries[0].contentRect;
      debouncedResizeUpdate(width, height);
    });
    newObserver.observe(chartContentEl);
    setChartContentResizeObserver(newObserver);
  };
  const updateIntersectionObserver = () => {
    if (progressiveIntersectionObserver) {
      progressiveIntersectionObserver.disconnect();
    }
    if (!progressiveMarkerRef.current) {
      return;
    }
    const newObserver = new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (entry.isIntersecting && contentIsTruncated) {
          setProgressivePage(progressivePage + 1);
        }
      }
    }, {
      root: null,
      threshold: 0.1
    });
    newObserver.observe(progressiveMarkerRef.current);
    setProgressiveIntersectionObserver(newObserver);
  };
  const onProgressiveMarkerInserted = (el: HTMLElement) => {
    if (!el) {
      return;
    }
    updateIntersectionObserver();
  };

  // On mount/cleanup
  useEffect(() => {
    onChartContentInserted(chartContentRef.current);
    onProgressiveMarkerInserted(progressiveMarkerRef.current);
    return () => {
      chartContentResizeObserver === null || chartContentResizeObserver === void 0 || chartContentResizeObserver.disconnect();
      progressiveIntersectionObserver === null || progressiveIntersectionObserver === void 0 || progressiveIntersectionObserver.disconnect();
    };
  }, []);
  useEffect(() => {
    updateIntersectionObserver();
  }, [contentIsTruncated, progressivePage]);
  useEffect(() => {
    setDefaultColumnWidths();
  }, [props.columnWidths]);
  useEffect(() => {
    if (props.hideTooltip) {
      setHoverTooltip(null);
    }
  }, [props.hideTooltip]);

  // Resize handlers
  useEffect(() => {
    const onResizeMouseUp = () => {
      var _props$onColumnWidthC;
      if (resizeColumnIdx === null) {
        return;
      }
      const columnElement = getHeaderColumn(resizeColumnIdx);
      const updatedColumnWidths = _objectSpread(_objectSpread({}, columnWidths), {}, {
        [resizeColumnIdx]: getPercentageWidth(columnElement.offsetWidth)
      });
      (_props$onColumnWidthC = props.onColumnWidthChange) === null || _props$onColumnWidthC === void 0 || _props$onColumnWidthC.call(props, updatedColumnWidths);
      setColumnWidths(updatedColumnWidths);

      // Reset state
      setResizeColumn(null);
      setResizeColumnIdx(null);
      setResizeColumnWidth(0);
      setResizeStartX(null);
    };
    const onResizeMouseMove = ev => {
      if (resizeStartX === null) {
        return;
      }
      ev.stopPropagation();
      const maximumColumnWidth = calculateMaxColumnWidth(resizeColumnIdx);
      const delta = ev.screenX - resizeStartX;
      let width = Math.max(columnWidths[resizeColumnIdx] + delta, MINIMUM_COLUMN_WIDTH);
      width = Math.min(width, maximumColumnWidth);
      setResizeColumnWidth(width);
    };
    window.addEventListener("mousemove", onResizeMouseMove, true);
    window.addEventListener("mouseup", onResizeMouseUp, true);
    return () => {
      window.removeEventListener("mousemove", onResizeMouseMove, true);
      window.removeEventListener("mouseup", onResizeMouseUp, true);
    };
  }, [columnWidths, resizeColumnIdx, resizeStartX, props.onColumnWidthChange]);

  // don't allow resizing if there's overflow
  function canResize(): boolean {
    const {
      labelWidthPercentage,
      valueWidthPercentage
    } = getRespColumnWidths();
    const depth = segmentDepth(chartData[0]) + 1;
    return depth * labelWidthPercentage + valueWidthPercentage <= 100;
  }
  const onResizeMouseDown = (ev, columnIdx) => {
    ev.preventDefault();
    if (ev.button !== 0) {
      return;
    }
    const columnElement = getHeaderColumn(columnIdx);
    const widthForColumn = columnElement.offsetWidth;
    const updatedColumnWidths = _objectSpread({}, columnWidths);
    updatedColumnWidths[columnIdx] = widthForColumn;
    setResizeColumnWidth(widthForColumn);
    setColumnWidths(updatedColumnWidths);
    setResizeColumn(columnElement);
    setResizeColumnIdx(columnIdx);
    setResizeStartX(ev.screenX);
  };
  const getValueWidth = () => {
    if (!canResize()) {
      return getPixelWidth(getRespColumnWidths().valueWidthPercentage);
    }
    return selfWidth - sum(getColumnWidths());
  };

  /**
   * mp-bar-viz renders markup in a recursive tree structure that mirrors its chartData prop.
   * since we store column widths as percentages relative to the entire chart, we need to translate
   * those values into be relative to the widths of the remaining columns.
   *
   * TODO: consider refactoring to render in a flat table-like structure to simplify this logic
   * NOTE: attempted to render in a single grid in this PR https://github.com/mixpanel/analytics/pull/35717
   * however, certain browsers impose limits on number of grid rows you can render
   * https://bugzilla.mozilla.org/show_bug.cgi?id=1336679
   * https://bugs.chromium.org/p/chromium/issues/detail?id=688640
   */
  const getNestedColumnGridTemplate = (columnIdx: number): string => {
    const {
      labelWidthPercentage,
      valueWidthPercentage
    } = getRespColumnWidths();
    if (!canResize()) {
      return "".concat(getPixelWidth(labelWidthPercentage), "px ").concat(getPixelWidth(valueWidthPercentage), "px");
    }
    const valueWidth = getValueWidth();
    const remainingColumnTotalWidth = sum([...getColumnWidths().slice(columnIdx + 1), valueWidth]);
    return "".concat(getColumnWidth(columnIdx), "px ").concat(remainingColumnTotalWidth, "px");
  };
  const initHideTooltipTimeout = () => {
    clearHideTooltipTimeout();
    hideTooltipTimeoutId.current = window.setTimeout(() => {
      setHoverTooltip(null);
    }, 250);
  };
  useEffect(() => {
    let numToShow;
    if (props.downloadMode) {
      numToShow = DOWNLOAD_SEGMENT_LIMIT;
    } else {
      const pageSize = getProgressivePageSize(selfHeight);
      numToShow = (progressivePage + 1) * pageSize;
    }
    const {
      isTruncated,
      segments
    } = truncateSegments(chartData, numToShow);
    setContentIsTruncated(isTruncated);
    setSegmentsToDisplay(segments);
  }, [chartData, props.downloadMode, progressivePage, selfHeight]);
  useEffect(() => {
    // This is expensive to do all the time, so do it in a useEffect
    const dataWithColors = assignColorsToSegmentArray(props.chartData);
    setChartData(dataWithColors);
    const maxLabelLength = getMaxLabelCharLengthFromSegments({
      segments: dataWithColors,
      isStacked: props.isStacked,
      negatives: false
    });
    const maxNegativeLabelLength = getMaxLabelCharLengthFromSegments({
      segments: dataWithColors,
      isStacked: props.isStacked,
      negatives: true
    });
    setMaxLabelCharLength(maxLabelLength);
    setMaxNegativeLabelCharLength(maxNegativeLabelLength);
    const updatedChartMax = dataWithColors.reduce((acc, segment) => {
      const sectionMax = maximumSegmentValue(segment);
      return sectionMax > acc ? sectionMax : acc;
    }, 0);
    const updatedChartMin = dataWithColors.reduce((acc, segment) => {
      const sectionMin = minimumSegmentValue(segment);
      return sectionMin < acc ? sectionMin : acc;
    }, Infinity);
    setDefaultColumnWidths();
    setChartMax(updatedChartMax);
    setChartMin(updatedChartMin);
  }, [props.chartData]);
  const renderHeaders = () => {
    const headers = generateHeaders(chartData[0], props.sortConfig);
    return headers.map((_ref2, idx) => {
      let {
        label,
        sortOptionItems
      } = _ref2;
      const headerLabelEl = <div className={styles.label}>
          <div className={styles.text}>{label}</div>
          {props.sortConfig && <div className={styles.sortIcon}>
              <SvgIconReact icon={"caret-down"} />
            </div>}
        </div>;
      return <div className={styles.header} key={"".concat(idx, "-").concat(label)} elref={"header"} ref={node => {
        const headerNodes = getHeaders();
        headerNodes[idx] = node;
      }}>
          {props.sortConfig ? <SelectReact sections={[{
          items: sortOptionItems
        }]} menuWidth={144} elref={"headerMenu"} onChange={ev => onSortingChange(ev, idx)} className={styles.mpSelect}>
              <div className={styles.sortTrigger} slot={"trigger"}>
                {headerLabelEl}
              </div>
            </SelectReact> : headerLabelEl}
        </div>;
    });
  };
  const renderResizeGrips = () => {
    if (!canResize()) {
      return null;
    }
    const headers = generateHeaders(chartData[0], props.sortConfig);
    return getColumnWidths().reduce((acc: {
      offsetWidth: number;
      nodes: Array<React.ReactNode>;
    }, _, idx) => {
      var _headers$idx;
      const offsetWidth = acc.offsetWidth + getColumnWidth(idx);
      const left = "".concat(offsetWidth - 12, "px");
      return {
        offsetWidth,
        nodes: acc.nodes.concat(<div className={clsx({
          [styles.headerResizeGrip]: true,
          'is-resizing': resizeColumnIdx === idx
        })} key={"".concat(idx, "-").concat((_headers$idx = headers[idx]) === null || _headers$idx === void 0 ? void 0 : _headers$idx.label)} style={{
          left
        }} onMouseDown={ev => onResizeMouseDown(ev, idx)} elref='resizeGrip' />)
      };
    }, {
      offsetWidth: 0,
      nodes: []
    }).nodes;
  };
  const clickBar = (segment: Segment, bar: Bar, barSegment: BarSegment, clickEvent: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    var _props$onBarClick;
    const {
      pageX,
      pageY
    } = clickEvent;
    (_props$onBarClick = props.onBarClick) === null || _props$onBarClick === void 0 || _props$onBarClick.call(props, {
      pageX,
      pageY,
      segment,
      bar,
      barSegment
    });
  };
  const renderBar = (_ref3: {
    segment: SegmentWithoutChildren;
    bar: Bar;
    isFirstOfPair: boolean;
    index: number;
    renderNegatives?: boolean;
  }) => {
    var _ref4;
    let {
      segment,
      bar,
      isFirstOfPair,
      index,
      /** render bar.negativeSegments instead of bar.segments */
      renderNegatives = false
    }: {
      segment: SegmentWithoutChildren;
      bar: Bar;
      isFirstOfPair: boolean;
      index: number;
      renderNegatives?: boolean;
    } = _ref3;
    if (renderNegatives && !bar.negativeSegments.length || !renderNegatives && !bar.segments.length) {
      return null;
    }
    const enableClick = props.enableBarClickEvents;
    const showSegmentLabels = props.showSegmentLabels;
    const throttledMouseMove = throttle((ev, barSegment) => onSegmentMouseMove(ev, segment, bar, barSegment), TOOLTIP_THROTTLE_TIME, {
      leading: true,
      trailing: false
    });
    const mouseLeave = () => {
      props.segmentFilterController.notifySeriesHovered(null);
      initHideTooltipTimeout();
    };
    const mouseMove = (ev, barSegment) => {
      var _barSegment$extras;
      const barPath = (_barSegment$extras = barSegment.extras) === null || _barSegment$extras === void 0 ? void 0 : _barSegment$extras.path;
      const segmentId = segment.bars.length > 1 ? barPath === null || barPath === void 0 ? void 0 : barPath.join(",") : barPath === null || barPath === void 0 ? void 0 : barPath.at(-1);
      props.segmentFilterController.notifySeriesHovered({
        seriesId: segmentId
      });
      throttledMouseMove(ev, barSegment);
    };
    const segments = renderNegatives ? bar.negativeSegments.toReversed() : bar.segments;
    const barLabel = (_ref4 = renderNegatives ? bar.negativeLabel : bar.label) !== null && _ref4 !== void 0 ? _ref4 : !showSegmentLabels ? segments[0].label : null;
    const segmentMax = renderNegatives ? chartMin : chartMax;
    const isStacked = segments.length > 1;
    let barGridColumn = "1";
    if (hasNegativeValues && !renderNegatives) {
      barGridColumn = "2";
    }
    return <div className={clsx({
      [styles.chartBar]: true,
      [styles.negative]: renderNegatives,
      [styles.firstOfPair]: isFirstOfPair
    })} key={"".concat(index, "-").concat(barLabel, "-").concat(renderNegatives)} style={{
      gridColumn: barGridColumn
    }} elref={"bar"} data-sentry-component="renderBar" data-sentry-source-file="index.tsx">
        {...segments.map((barSegment, idx) => {
        var _props$selectedBarSeg;
        const {
          widthAsPercentage
        } = getBarChartDisplayValues({
          value: barSegment.value,
          percent: barSegment.value / chartMax || 0,
          segmentMax,
          barValueType: props.barValueType
        });
        const isVirtuallyHovered = isStacked && matchesHoveredPath(barSegment.extras.path);
        const segmentLabel = showSegmentLabels ? <div className={styles.barSegmentLabelWrapper}>
              {/* Empty div used to push inner label to the edge and overflow to next line when there isn't enough space */}
              {/* This way, we can hide overflowing labels completely using just CSS */}
              <div className={styles.barSegmentLabelNudger} />
              <div className={styles.barSegmentLabel} elref={"barSegmentLabel"}>
                {barSegment.label}
              </div>
            </div> : null;
        return <div style={{
          width: "".concat(widthAsPercentage * 100, "%")
        }} key={"".concat(index, "-").concat(idx, "-").concat(renderNegatives)} onMouseMove={ev => mouseMove(ev, barSegment)} onMouseLeave={mouseLeave} onClick={enableClick ? ev => clickBar(segment, bar, barSegment, ev) : () => {}} className={clsx({
          [styles.barSegmentWrapper]: true,
          [styles.clickable]: enableClick,
          [styles.selected]: isEqual((_props$selectedBarSeg = props.selectedBarSegment) === null || _props$selectedBarSeg === void 0 ? void 0 : _props$selectedBarSeg.extras, barSegment === null || barSegment === void 0 ? void 0 : barSegment.extras),
          [styles.virtualHover]: isVirtuallyHovered
          /** @todo (consistent-coloring) make segments dim to match highlights styling on hover  */
        })} elref={"barSegment"}>
              <div className={styles.barSegment} style={_objectSpread(_objectSpread({}, getBarBackground(barSegment.pattern, barSegment.color)), {}, {
            width: "100%"
          })} />
              {segmentLabel}
              {idx === segments.length - 1 && <div className={clsx({
            [styles.barLabel]: true,
            [styles.negative]: renderNegatives
          })} elref={"barLabel"}>
                  {barLabel}
                </div>}
            </div>;
      })}
      </div>;
  };
  const renderFinalSegmentBar = (segment: SegmentWithoutChildren, columnIdx: number) => {
    var _firstBarAllSegments$, _firstBarAllSegments$2;
    let barTemplateColumns = "100%";
    const labelTooltip = getTooltipFromLabel(segment.label);
    if (hasNegativeValues) {
      const dist = Math.abs(chartMin - chartMax);
      const percentageOfDist = num => Math.abs(num) / dist * 100;
      barTemplateColumns = "".concat(percentageOfDist(chartMin), "% ").concat(percentageOfDist(chartMax), "%");
    }
    const firstBarAllSegments = [...segment.bars[0].negativeSegments, ...segment.bars[0].segments];
    const isStacked = firstBarAllSegments.length > 1;
    const segmentId = (_firstBarAllSegments$ = firstBarAllSegments[0].extras) === null || _firstBarAllSegments$ === void 0 || (_firstBarAllSegments$ = _firstBarAllSegments$.path) === null || _firstBarAllSegments$ === void 0 ? void 0 : _firstBarAllSegments$.join(",");
    // only apply virtual hover here if it's not a stacked bar
    const isVirtuallyHovered = !isStacked && matchesHoveredPath((_firstBarAllSegments$2 = firstBarAllSegments[0].extras) === null || _firstBarAllSegments$2 === void 0 ? void 0 : _firstBarAllSegments$2.path);
    return <div className={clsx({
      [styles.segmentWrapper]: true,
      [styles.barsWrapper]: true,
      [styles.virtualHover]: isVirtuallyHovered
    })} key={"".concat(segment.label, "-").concat(columnIdx)} style={{
      gridTemplateColumns: getNestedColumnGridTemplate(columnIdx)
    }} elref={"leafSegment"} onMouseEnter={!isStacked ? () => props.segmentFilterController.notifySeriesHovered({
      seriesId: segmentId
    }) : () => {}} onMouseLeave={!isStacked ? () => props.segmentFilterController.notifySeriesHovered(null) : () => {}} data-sentry-component="renderFinalSegmentBar" data-sentry-source-file="index.tsx">
        <div className={clsx({
        [styles.segmentLabelBlock]: true,
        [styles.multiBar]: segment.bars.length > 1
      })} title={segment.label}>
          <div className={styles.segmentLabel}>{renderSegmentLabel(segment.label)}</div>
          <div className={styles.segmentValueLabel}>{segment.valueLabel}</div>
          {labelTooltip && <TooltipReact text={labelTooltip} placement={Placement.RightCenter} delayOut={150} />}
        </div>
        <div className={clsx({
        [styles.segmentValuesWrapper]: true,
        [styles.bars]: true,
        [styles.hasNegativeValues]: hasNegativeValues,
        [styles.stacked]: isStacked
      })} style={{
        gridTemplateColumns: barTemplateColumns
      }}>
          {segment.bars.map((bar: Bar, idx) => {
          // if there's exactly two bars, we render the first one with reversed labels
          // to have a side-by-side visualization.
          // (i.e. for stacked bar, the comparison bar's segment labels are on top)
          const isFirstOfPair = idx === 0 && segment.bars.length === 2;

          // in order to ensure the subsequent bar is on the next line, add an empty div if first bar is negative.
          // this fixes a case where a negative and positive bar appear on the same line (ie if the comparison bar is
          // negative, the main bar should still be on the next line).
          const shouldAddPlaceholder = idx % 2 === 0 && !isStacked && bar.negativeSegments.length;

          // rendered twice because stacked mode bars may have both segments and negativeSegments, which are rendered on the same line
          const barEl = <>
                {renderBar({
              segment,
              bar,
              isFirstOfPair,
              index: idx,
              renderNegatives: true
            })}
                {renderBar({
              segment,
              bar,
              isFirstOfPair,
              index: idx
            })}
              </>;
          if (shouldAddPlaceholder) {
            return <React.Fragment key={"".concat(columnIdx, "-").concat(idx)}>
                  {barEl}
                  <div />
                </React.Fragment>;
          }
          return barEl;
        })}
        </div>
      </div>;
  };
  function renderSegmentWithChildren(segment: SegmentWithChildren, columnIdx: number) {
    const isLeaf = !segment.children.some(segmentHasChildren);
    return <div className={clsx(styles.segmentWrapper, styles.breakdown)} key={"".concat(segment.label, "-").concat(columnIdx)} style={{
      gridTemplateColumns: getNestedColumnGridTemplate(columnIdx)
    }} data-sentry-component="renderSegmentWithChildren" data-sentry-source-file="index.tsx">
        <div className={clsx(styles.segmentLabelBlock, styles.breakdown)} title={segment.label}>
          <div className={styles.segmentLabel}>{renderSegmentLabel(segment.label)}</div>
          {segment.valueLabel && <div className={styles.segmentValueLabel}>
              <span className={styles.indicator}>Overall</span>
              <span className={styles.value}>{segment.valueLabel}</span>
            </div>}
        </div>
        <div className={clsx({
        [styles.segmentValuesWrapper]: true,
        [styles.breakdown]: true,
        [styles.leafSegment]: isLeaf
      })}>
          {segment.children.map(seg => getSegmentRenderer(seg, columnIdx + 1))}
        </div>
      </div>;
  }
  function getSegmentRenderer(segment: Segment, columnIdx) {
    return segmentHasChildren(segment) ? renderSegmentWithChildren(segment, columnIdx) : renderFinalSegmentBar(segment, columnIdx);
  }
  const headerColumnsWidths = getColumnWidths().map(width => "".concat(width, "px"));
  const isDownloadMode = props.downloadMode;
  const isHidden = !selfWidth;
  const isResizing = !!resizeColumn;
  return <div className={styles.root} elref='barViz' ref={rootRef} data-sentry-component="BarViz" data-sentry-source-file="index.tsx">
      <div className={clsx({
      [styles.barChart]: true,
      [styles.isResizing]: isResizing,
      // width may not have been calculated on first render, so we set visibility hidden
      [styles.isHidden]: isHidden
    })} style={{
      // Make sure the label size scales up based on potentially long labels
      // with custom units. We scale to the max of all labels so that the
      // bar sizes are consistent.
      '--text-label-width': "".concat(maxLabelCharLength * PX_PER_CHAR_LENGTH + BAR_LABEL_PADDING * 2, "px"),
      '--negative-text-label-width': "".concat(maxNegativeLabelCharLength * PX_PER_CHAR_LENGTH + BAR_LABEL_PADDING * 2, "px"),
      '--min-bar-segment-width': props.isStacked ? "unset" : "".concat(MIN_BAR_SEGMENT_WIDTH, "px")
    }}>
        {renderResizeGrips()}
        <div className={styles.chartContent} elref={"chartContent"} ref={chartContentRef}>
          <div className={styles.headerBar} style={{
          gridTemplateColumns: headerColumnsWidths.join(" ").concat(" ".concat(getValueWidth(), "px"))
        }}>
            {renderHeaders()}
          </div>

          <div>{segmentsToDisplay.map(segment => getSegmentRenderer(segment, 0))}</div>

          {!!isDownloadMode && contentIsTruncated ? <div className={styles.truncationNotice}>
              Results have been truncated to the first {DOWNLOAD_SEGMENT_LIMIT} segments for download.
            </div> : <div className={styles.progressiveMarker} ref={progressiveMarkerRef} />}
        </div>

        {hoverTooltip && <ChartTooltip {...hoverTooltip.props} />}
      </div>
    </div>;
};
export const BarVizReact = withStyles(BarViz, css);